import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { StoreButtons, BoutiqueCTA } from "../StoreButtons";
import { API, mediaUrl, focalStyle } from "../../lib/api";

const lineReveal = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
    }),
};

export function Hero() {
    const { data } = useSite();
    const hero = data?.sections?.hero || {};
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

    const lines = (hero.title || "").split("\n").filter(Boolean);
    const balanceImg = data?.featured_product?.image;
    const hasImg = !!hero.image?.url;
    const contain = hero.image_fit === "contain";

    return (
        <section ref={ref} className="relative min-h-screen bg-ivory overflow-hidden flex items-center" data-testid="hero-section">
            <div className="absolute -top-48 -right-48 w-[42rem] h-[42rem] rounded-full bg-gold/15 blur-3xl animate-pulse-glow" aria-hidden="true" />
            <div className="absolute -bottom-64 -left-48 w-[36rem] h-[36rem] rounded-full bg-forest/8 blur-3xl" aria-hidden="true" />
            <div className={`container-bc relative z-10 grid grid-cols-1 ${hasImg ? "lg:grid-cols-12" : ""} gap-12 items-center pt-28 pb-20 lg:pt-36`}>
                <div className={hasImg ? "lg:col-span-7" : "max-w-3xl"}>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="eyebrow flex items-center gap-2 mb-6"
                    >
                        <Sparkles size={14} /> {hero.eyebrow || "Coaching nouvelle génération"}
                    </motion.p>
                    <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink text-balance">
                        {lines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className="block">
                                    {i === lines.length - 1 ? <span className="text-forest">{line}</span> : line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.7 }}
                        className="mt-6 text-base sm:text-lg text-ink-soft leading-relaxed max-w-xl"
                        data-testid="hero-subtitle"
                    >
                        {hero.subtitle}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7 }}
                        className="mt-9 flex flex-col gap-5"
                    >
                        <StoreButtons size="lg" />
                        <BoutiqueCTA />
                    </motion.div>
                </div>
                {hasImg && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-5 relative"
                    >
                        <motion.div style={{ y: imgY }} className="relative">
                            <div className="absolute -inset-3 rounded-[2rem] border border-gold/50 translate-x-4 translate-y-4" aria-hidden="true" />
                            <img
                                src={mediaUrl(hero.image.url)}
                                alt={hero.image.alt || hero.title || "BODYCOACHIA"}
                                style={{
                                    ...(contain ? {} : focalStyle(hero.image)),
                                    ...(contain && hero.image.width && hero.image.height ? { aspectRatio: `${hero.image.width} / ${hero.image.height}` } : {}),
                                }}
                                className={`relative rounded-[2rem] w-full gold-frame ${contain ? "object-contain bg-pearl" : "object-cover aspect-[4/5]"}`}
                                loading="eager"
                                data-testid="hero-image"
                            />
                            {balanceImg && (
                                <Link
                                    to="/boutique/balance-bodycoachia"
                                    className="absolute -bottom-6 -left-4 sm:-left-8 card-light p-3 flex items-center gap-3 w-64 hover:-translate-y-1 transition-transform"
                                    data-testid="hero-balance-card"
                                >
                                    <img
                                        src={`${API}/shop-media/${data.featured_product.product_id}/${balanceImg.id}`}
                                        alt="Balance connectée BODYCOACHIA"
                                        className="w-14 h-14 rounded-xl object-cover"
                                        loading="lazy"
                                    />
                                    <span>
                                        <span className="block text-[10px] font-mono uppercase tracking-widest text-gold-dark">Nouveau</span>
                                        <span className="block text-sm font-semibold text-forest leading-tight">Balance connectée 8 électrodes</span>
                                    </span>
                                </Link>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
